import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Target, Users } from "lucide-react";
import { CTABand } from "../components/CTABand";
import { useSEO } from "../lib/useSEO";

const values = [
  { icon: ShieldCheck, title: "Integrity First", desc: "Honest assessments. Clear pricing. Transparent reporting. Every contract starts and ends with trust." },
  { icon: Target, title: "Outcome-Led", desc: "We measure ourselves against incidents prevented, response times and client confidence — not hours billed." },
  { icon: Award, title: "Accredited & Audited", desc: "SIA-licensed, ICO-registered, Constructionline Gold and SSIP — independently verified." },
  { icon: Users, title: "People + Tech", desc: "Real officers, modern systems, and a UK control room operating as one team." },
];

export default function AboutPage() {
  useSEO({
    title: "About USI Security — UK SIA-Licensed Security Company",
    description: "USI Security Ltd (Ultimate Solutions In Security) is a specialist UK private security provider. SIA Approved Contractor, Constructionline Gold, SSIP-accredited. Trusted to protect critical UK infrastructure.",
    keywords: "about USI Security, UK security company, SIA approved contractor, Constructionline Gold security, SSIP security, Achilles UVDB security, private security firm UK",
    path: "/about",
  });
  return (
    <div data-testid="about-page" className="pt-24">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 border-b border-white/10">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(6,182,212,0.1),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">◆ About USI Security</p>
            <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tighter">
              Quietly protecting<br />
              <span className="text-gradient-cyan">Britain's critical sites.</span>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-slate-400 leading-relaxed">
              USI Security is a specialist UK provider of end-to-end critical infrastructure security. We combine SIA-licensed officers, AI-augmented monitoring and certified K9 teams to safeguard the substations, sites and events the country relies on.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">Our Story</p>
          <h2 className="font-display font-extrabold text-white text-3xl lg:text-4xl tracking-tighter leading-tight">
            Built by operators.<br />Trusted by operators.
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-4 text-slate-300 leading-relaxed">
          <p>USI Security was founded to bring genuine operational rigour to UK critical infrastructure protection. Too many providers treat security as a commodity — a body at a gate, a camera on a pole. We don't.</p>
          <p>We treat security as engineering: layered systems, briefed teams, measurable outcomes. Every contract is led by an accountable consultant, every site receives bespoke assignment instructions, and every alert is verified by a UK-based control room before escalation.</p>
          <p>From the smallest pop-up event to multi-site portfolio coverage, our approach is the same: design carefully, deploy professionally, report transparently.</p>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-white/10 bg-ink-900/40 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">◆ What guides us</p>
          <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tighter leading-tight max-w-3xl mb-12">
            Four principles. <span className="text-gradient-cyan">Zero compromises.</span>
          </h2>

          <div className="grid sm:grid-cols-2 gap-5">
            {values.map((v, i) => (
              <div key={v.title} className="card-tech rounded-xl p-7 flex gap-5">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
                  <v.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">0{i + 1}</div>
                  <h3 className="font-display font-bold text-white text-xl tracking-tight mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
